import Card from "@/components/common/Card";
import { Switch } from "@headlessui/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

interface NotificationSettingsProps {
  user: any;
}

const NotificationSettings = ({ }: NotificationSettingsProps) => {
  const { t } = useTranslation();
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    browser: true,
    courseAssignments: true,
    policyUpdates: true,
    securityAlerts: true,
    learningReminders: false,
  });

  const handleNotificationToggle = (
    setting: keyof typeof notificationSettings
  ) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
    toast.success(t("settings.notificationUpdated"));
  };

  const notificationChannels = [
    {
      key: "email" as const,
      label: t("settings.emailNotifications"),
      description: t("settings.emailNotificationsDescription"),
    },
    {
      key: "browser" as const,
      label: t("settings.browserNotifications"),
      description: t("settings.browserNotificationsDescription"),
    },
  ];

  const notificationTypes = [
    {
      key: "courseAssignments" as const,
      label: t("settings.courseAssignments"),
      description: t("settings.courseAssignmentsDescription"),
    },
    {
      key: "policyUpdates" as const,
      label: t("settings.policyUpdates"),
      description: t("settings.policyUpdatesDescription"),
    },
    {
      key: "securityAlerts" as const,
      label: t("settings.securityAlerts"),
      description: t("settings.securityAlertsDescription"),
    },
    {
      key: "learningReminders" as const,
      label: t("settings.learningReminders"),
      description: t("settings.learningRemindersDescription"),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
        {t("settings.notifications")}
      </h2>

      <div className="space-y-6">
        {/* Notification Channels */}
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.notificationChannels")}
          </h3>
          <div className="space-y-4">
            {notificationChannels.map((channel) => (
              <div key={channel.key} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {channel.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {channel.description}
                  </p>
                </div>
                <Switch
                  checked={notificationSettings[channel.key]}
                  onChange={() => handleNotificationToggle(channel.key)}
                  className={`${
                    notificationSettings[channel.key]
                      ? "bg-primary-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      notificationSettings[channel.key]
                        ? "translate-x-6"
                        : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            ))}
          </div>
        </Card>

        {/* Notification Types */}
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.notificationTypes")}
          </h3>
          <div className="space-y-4">
            {notificationTypes.map((type) => (
              <div key={type.key} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {type.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {type.description}
                  </p>
                </div>
                <Switch
                  checked={notificationSettings[type.key]}
                  onChange={() => handleNotificationToggle(type.key)}
                  className={`${
                    notificationSettings[type.key]
                      ? "bg-primary-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      notificationSettings[type.key]
                        ? "translate-x-6"
                        : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

export default NotificationSettings;

